import { useState } from 'react';
import uuid from 'uuid';
import Button from '../Button';
import styles from './Table.module.css';

export type Header = {
  label: string;
  key: string;
  hide?: boolean;
  type?: 'header' | 'cell';
};

export type Row = {
  cells: {
    value: string;
    key: string;
    isAction?: boolean;
    onClick?: (data: unknown) => void;
    actions?: { label: string; onClick: (data: unknown) => void; key: string }[];
  }[];
  headers?: Header[];
  isCollapsible?: boolean;
  children?: Row[];
  extraData?: unknown;
};

export const formatTableData = (
  data: object[],
  headers: Header[],
  options: {
    transformArraysFunction?: (value: unknown, key: string) => string;
    childrenAreCollapsible?: boolean;
    addActions?: boolean;
    actions?: { label: string; onClick: (data: unknown) => void }[];
  },
  level = 0
) => {
  const formattedData: Row[] = [];
  for (const row of data) {
    const formattedRow: Row = {
      cells: headers
        .filter((header) => header.type !== 'header')
        .map((header) => ({
          value: options.transformArraysFunction
            ? options.transformArraysFunction(row[header.key as keyof typeof row], header.key)
            : row[header.key as keyof typeof row],
          key: uuid.v4(),
        })),
      extraData: row,
    };
    if (options.addActions) {
      formattedRow.cells.push({
        value: '',
        key: uuid.v4(),
        isAction: true,
        actions: options.actions?.map((action, index) => ({
          label: action.label,
          key: uuid.v4(),
          onClick: action.onClick,
        })),
      });
    }
    if ('children' in row) {
      formattedRow.children = formatTableData(
        (row as { children: object[] }).children,
        headers,
        options,
        level + 1
      );
      formattedRow.isCollapsible = options.childrenAreCollapsible ?? false;
    }
    formattedData.push(formattedRow);
  }
  return formattedData;
};

function LinkedRows({
  rows,
  isCollapsible,
  headers,
  options,
}: {
  rows: { parent: Row; children: Row[] };
  isCollapsible: boolean;
  headers: Header[];
  options: { hideHeaders?: boolean; collapsed?: boolean };
}) {
  const [isCollapsed, setIsCollapsed] = useState<boolean>(
    isCollapsible ? options.collapsed ?? true : false
  );
  return (
    <>
      <tr
        key={`${uuid.v4()}`}
        className={styles.collapsibleController}
        onClick={isCollapsible ? () => setIsCollapsed((prev) => !prev) : undefined}
      >
        {rows.parent.cells.map((cell) =>
          cell.isAction ? (
            <td key={cell.key} className={styles.actionCell}>
              {cell.actions?.map((action) => (
                <Button
                  variant="outlined"
                  size="medium"
                  key={action.key}
                  onClick={(event) => {
                    event.stopPropagation();
                    action.onClick({ event, source: rows.parent });
                  }}
                >
                  {action.label}
                </Button>
              ))}
            </td>
          ) : (
            <td key={cell.key}>{cell.value}</td>
          )
        )}
      </tr>
      <tr key={`${uuid.v4()}`} className={styles.collapsibleRow}>
        <td colSpan={100} className={isCollapsed ? styles.collapsedContent : ''}>
          <CollapsibleTable
            key={`${uuid.v4()}`}
            rows={rows.children ?? []}
            headers={headers}
            options={options}
          />
        </td>
      </tr>
    </>
  );
}

export default function CollapsibleTable({
  rows,
  headers,
  options,
  paginated = false,
  loadNextPage,
  state,
  hasNextPage = false,
  refetch,
}: {
  rows: Row[];
  headers: Header[];
  options: { hideHeaders?: boolean; hideInnerHeaders?: boolean; collapsed?: boolean };
  paginated?: boolean;
  loadNextPage?: () => void;
  paginationLoading?: boolean;
  state?: 'loading' | 'error' | 'success';
  hasNextPage?: boolean;
  refetch?: () => void;
}) {
  return (
    <div className={styles.tableContainer}>
      <table className={styles.collapsibleTable}>
        <thead className={options.hideHeaders ? styles.srOnly : ''}>
          <tr>
            {headers.map((header) => (
              <th key={`${uuid.v4()}`}>{header.hide ? <></> : header.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {state === 'error' && (
            <tr key="error-fetching-data">
              <td className={styles.error} colSpan={100}>
                There was an error fetching the data, try again latter.
              </td>
            </tr>
          )}
          {state === 'success' && rows.length === 0 ? (
            <tr key="info-no-data">
              <td className={styles.info} colSpan={100}>
                <div>
                  <span>No data to display.</span>
                  {refetch && (
                    <Button variant="ghost" size="medium" key="refetch" onClick={refetch}>
                      Click to refresh Data.
                    </Button>
                  )}
                </div>
              </td>
            </tr>
          ) : (
            rows.map((row, index) => (
              <>
                {row.children?.length ? (
                  <LinkedRows
                    key={`${uuid.v4()}`}
                    rows={{ parent: row, children: row.children }}
                    isCollapsible={row.isCollapsible ?? true}
                    headers={headers}
                    options={{
                      hideHeaders: options.hideInnerHeaders,
                      collapsed: options.collapsed,
                    }}
                  />
                ) : (
                  <tr key={`${uuid.v4()}`}>
                    {row.cells.map((cell) =>
                      cell.isAction ? (
                        <td key={cell.key} id={cell.key} className={styles.actionCell}>
                          {cell.actions?.map((action) => (
                            <Button
                              variant="outlined"
                              size="medium"
                              key={action.key}
                              id={action.key}
                              onClick={(event) => {
                                event.stopPropagation();
                                action.onClick({ event, source: row });
                              }}
                            >
                              {action.label}
                            </Button>
                          ))}
                        </td>
                      ) : (
                        <td key={cell.key} id={cell.key}>
                          {cell.value}
                        </td>
                      )
                    )}
                  </tr>
                )}
              </>
            ))
          )}
        </tbody>
      </table>
      {paginated && state !== 'error' && rows.length > 0 && hasNextPage && (
        <div className={styles.paginationContainer}>
          {state === 'loading' ? (
            <div key="loading" className={styles.paginationButton}>
              Loading...
            </div>
          ) : (
            <Button key="load-more" variant="ghost" size="full" onClick={loadNextPage}>
              Load More
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
